import { useState, useEffect } from 'react';
import { MapPin, Activity, Save, Eye, EyeOff, CheckCircle, XCircle, MessageSquare, Send, Key, Bot, Cloud, Phone, Banknote, Mail, Sparkles } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { sendSMS, formatPhoneNumber } from '../../lib/gohighlevel';
import { useToast } from '../../contexts/ToastContext';
import { testSamsaraConnection } from '../../services/samsaraService';
import ELDConnectionsManager from './ELDConnectionsManager';
import { IntegrationsOverview } from './IntegrationsOverview';

export function IntegrationsView() {
  const [samsaraApiKey, setSamsaraApiKey] = useState('');
  const [samsaraEnabled, setSamsaraEnabled] = useState(false);
  const [samsaraConnected, setSamsaraConnected] = useState(false);
  const [showSamsaraKey, setShowSamsaraKey] = useState(false);

  const [eldApiKey, setEldApiKey] = useState('');
  const [eldApiUrl, setEldApiUrl] = useState('');
  const [eldProvider, setEldProvider] = useState('');
  const [eldEnabled, setEldEnabled] = useState(false);
  const [eldConnected, setEldConnected] = useState(false);
  const [showEldKey, setShowEldKey] = useState(false);
  
  // PRO LOGS specific fields
  const [eldProLogsName, setEldProLogsName] = useState('');
  const [eldProLogsId, setEldProLogsId] = useState('');
  const [eldProLogsSecret, setEldProLogsSecret] = useState('');
  const [showProLogsSecret, setShowProLogsSecret] = useState(false);

  const [saving, setSaving] = useState(false);

  const toast = useToast();

  // GoHighLevel SMS settings
  const [ghlApiKey, setGhlApiKey] = useState('');
  const [ghlLocationId, setGhlLocationId] = useState('');
  const [ghlEnabled, setGhlEnabled] = useState(false);
  const [ghlConnected, setGhlConnected] = useState(false);
  const [showGhlKey, setShowGhlKey] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [loading, setLoading] = useState(true);

  // ChatGPT AI Assistant settings
  const [chatgptApiKey, setChatgptApiKey] = useState('');
  const [chatgptEnabled, setChatgptEnabled] = useState(false);
  const [chatgptConnected, setChatgptConnected] = useState(false);
  const [showChatgptKey, setShowChatgptKey] = useState(false);
  const [chatgptModel, setChatgptModel] = useState('gpt-4o-mini');

  // Google Gemini AI settings (used for direct PDF/document parsing)
  const [geminiApiKey, setGeminiApiKey] = useState('');
  const [geminiEnabled, setGeminiEnabled] = useState(false);
  const [geminiConnected, setGeminiConnected] = useState(false);
  const [showGeminiKey, setShowGeminiKey] = useState(false);
  const [geminiModel, setGeminiModel] = useState('gemini-2.5-flash');

  // Google Document AI settings
  const [gcpProjectId, setGcpProjectId] = useState('');
  const [gcpProcessorId, setGcpProcessorId] = useState('');
  const [gcpServiceAccountEmail, setGcpServiceAccountEmail] = useState('');
  const [gcpPrivateKey, setGcpPrivateKey] = useState('');
  const [documentAIEnabled, setDocumentAIEnabled] = useState(false);
  const [documentAIConnected, setDocumentAIConnected] = useState(false);
  const [showGcpKey, setShowGcpKey] = useState(false);

  // OpenWeatherMap settings
  const [openWeatherApiKey, setOpenWeatherApiKey] = useState('');
  const [openWeatherEnabled, setOpenWeatherEnabled] = useState(false);
  const [openWeatherConnected, setOpenWeatherConnected] = useState(false);
  const [showOpenWeatherKey, setShowOpenWeatherKey] = useState(false);

  // RingCentral settings
  const [ringCentralClientId, setRingCentralClientId] = useState('');
  const [ringCentralClientSecret, setRingCentralClientSecret] = useState('');
  const [ringCentralJwt, setRingCentralJwt] = useState('');
  const [ringCentralEnabled, setRingCentralEnabled] = useState(false);
  const [ringCentralConnected, setRingCentralConnected] = useState(false);
  const [showRingCentralSecret, setShowRingCentralSecret] = useState(false);
  const [showRingCentralJwt, setShowRingCentralJwt] = useState(false);

  // Google Maps API settings
  const [googleMapsApiKey, setGoogleMapsApiKey] = useState('');
  const [googleMapsEnabled, setGoogleMapsEnabled] = useState(false);
  const [googleMapsConnected, setGoogleMapsConnected] = useState(false);
  const [showGoogleMapsKey, setShowGoogleMapsKey] = useState(false);

  // TruFund Factoring settings
  const [trufundUsername, setTrufundUsername] = useState('');
  const [trufundToken, setTrufundToken] = useState('');
  const [trufundEnabled, setTrufundEnabled] = useState(false);
  const [trufundConnected, setTrufundConnected] = useState(false);
  const [showTrufundToken, setShowTrufundToken] = useState(false);

  // Resend Email settings
  const [resendApiKey, setResendApiKey] = useState('');
  const [resendFromEmail, setResendFromEmail] = useState('');
  const [resendEnabled, setResendEnabled] = useState(false);
  const [resendConnected, setResendConnected] = useState(false);
  const [showResendKey, setShowResendKey] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  const [emailTestResult, setEmailTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [isTestingEmail, setIsTestingEmail] = useState(false);

  // Load settings from database
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const { data, error } = await supabase
        .from('system_settings')
        .select('setting_key, setting_value')
        .in('setting_key', [
          'gohighlevel_api_key', 
          'gohighlevel_location_id', 
          'gohighlevel_enabled',
          'samsara_api_key',
          'samsara_enabled',
          'chatgpt_api_key',
          'chatgpt_enabled',
          'chatgpt_model',
          'gemini_api_key',
          'gemini_enabled',
          'gemini_model',
          'gcp_project_id',
          'gcp_processor_id',
          'gcp_service_account_email',
          'gcp_private_key',
          'document_ai_enabled',
          'openweather_api_key',
          'openweather_enabled',
          'ringcentral_client_id',
          'ringcentral_client_secret',
          'ringcentral_jwt',
          'ringcentral_enabled',
          'eld_api_key',
          'eld_api_url',
          'eld_provider',
          'eld_enabled',
          'eld_prologs_name',
          'eld_prologs_id',
          'eld_prologs_secret',
          'google_maps_api_key',
          'google_maps_enabled',
          'trufund_username',
          'trufund_token',
          'trufund_enabled',
          'resend_api_key',
          'resend_from_email',
          'resend_enabled'
        ]);

      if (error) throw error;

      if (data) {
        data.forEach(setting => {
          if (setting.setting_key === 'gohighlevel_api_key') {
            setGhlApiKey(setting.setting_value || '');
            setGhlConnected(!!setting.setting_value);
          }
          if (setting.setting_key === 'gohighlevel_location_id') {
            setGhlLocationId(setting.setting_value || '');
          }
          if (setting.setting_key === 'gohighlevel_enabled') {
            setGhlEnabled(setting.setting_value === 'true');
          }
          if (setting.setting_key === 'samsara_api_key') {
            setSamsaraApiKey(setting.setting_value || '');
            setSamsaraConnected(!!setting.setting_value);
          }
          if (setting.setting_key === 'samsara_enabled') {
            setSamsaraEnabled(setting.setting_value === 'true');
          }
          if (setting.setting_key === 'chatgpt_api_key') {
            setChatgptApiKey(setting.setting_value || '');
            setChatgptConnected(!!setting.setting_value);
          }
          if (setting.setting_key === 'chatgpt_enabled') {
            setChatgptEnabled(setting.setting_value === 'true');
          }
          if (setting.setting_key === 'chatgpt_model') {
            setChatgptModel(setting.setting_value || 'gpt-4o-mini');
          }
          if (setting.setting_key === 'gemini_api_key') {
            setGeminiApiKey(setting.setting_value || '');
            setGeminiConnected(!!setting.setting_value);
          }
          if (setting.setting_key === 'gemini_enabled') {
            setGeminiEnabled(setting.setting_value === 'true');
          }
          if (setting.setting_key === 'gemini_model') {
            // Upgrade a retired saved model so the dropdown lands on a valid one.
            const saved = setting.setting_value || '';
            const upgraded = ['gemini-2.0-flash', 'gemini-2.0-flash-exp', 'gemini-1.5-flash'].includes(saved)
              ? 'gemini-2.5-flash'
              : ['gemini-1.5-pro', 'gemini-pro'].includes(saved)
              ? 'gemini-2.5-pro'
              : saved || 'gemini-2.5-flash';
            setGeminiModel(upgraded);
          }
          if (setting.setting_key === 'gcp_project_id') {
            setGcpProjectId(setting.setting_value || '');
          }
          if (setting.setting_key === 'gcp_processor_id') {
            setGcpProcessorId(setting.setting_value || '');
          }
          if (setting.setting_key === 'gcp_service_account_email') {
            setGcpServiceAccountEmail(setting.setting_value || '');
          }
          if (setting.setting_key === 'gcp_private_key') {
            setGcpPrivateKey(setting.setting_value || '');
            setDocumentAIConnected(!!setting.setting_value);
          }
          if (setting.setting_key === 'document_ai_enabled') {
            setDocumentAIEnabled(setting.setting_value === 'true');
          }
          if (setting.setting_key === 'openweather_api_key') {
            setOpenWeatherApiKey(setting.setting_value || '');
            setOpenWeatherConnected(!!setting.setting_value);
          }
          if (setting.setting_key === 'openweather_enabled') {
            setOpenWeatherEnabled(setting.setting_value === 'true');
          }
          if (setting.setting_key === 'ringcentral_client_id') {
            setRingCentralClientId(setting.setting_value || '');
          }
          if (setting.setting_key === 'ringcentral_client_secret') {
            setRingCentralClientSecret(setting.setting_value || '');
          }
          if (setting.setting_key === 'ringcentral_jwt') {
            setRingCentralJwt(setting.setting_value || '');
            setRingCentralConnected(!!setting.setting_value);
          }
          if (setting.setting_key === 'ringcentral_enabled') {
            setRingCentralEnabled(setting.setting_value === 'true');
          }
          if (setting.setting_key === 'eld_api_key') {
            setEldApiKey(setting.setting_value || '');
            setEldConnected(!!setting.setting_value);
          }
          if (setting.setting_key === 'eld_api_url') {
            setEldApiUrl(setting.setting_value || '');
          }
          if (setting.setting_key === 'eld_provider') {
            setEldProvider(setting.setting_value || '');
          }
          if (setting.setting_key === 'eld_enabled') {
            setEldEnabled(setting.setting_value === 'true');
          }
          if (setting.setting_key === 'eld_prologs_name') {
            setEldProLogsName(setting.setting_value || '');
          }
          if (setting.setting_key === 'eld_prologs_id') {
            setEldProLogsId(setting.setting_value || '');
          }
          if (setting.setting_key === 'eld_prologs_secret') {
            setEldProLogsSecret(setting.setting_value || '');
          }
          if (setting.setting_key === 'google_maps_api_key') {
            setGoogleMapsApiKey(setting.setting_value || '');
            setGoogleMapsConnected(!!setting.setting_value);
          }
          if (setting.setting_key === 'google_maps_enabled') {
            setGoogleMapsEnabled(setting.setting_value === 'true');
          }
          if (setting.setting_key === 'trufund_username') {
            setTrufundUsername(setting.setting_value || '');
          }
          if (setting.setting_key === 'trufund_token') {
            setTrufundToken(setting.setting_value || '');
            setTrufundConnected(!!setting.setting_value);
          }
          if (setting.setting_key === 'trufund_enabled') {
            setTrufundEnabled(setting.setting_value === 'true');
          }
          if (setting.setting_key === 'resend_api_key') {
            setResendApiKey(setting.setting_value || '');
            setResendConnected(!!setting.setting_value);
          }
          if (setting.setting_key === 'resend_from_email') {
            setResendFromEmail(setting.setting_value || '');
          }
          if (setting.setting_key === 'resend_enabled') {
            setResendEnabled(setting.setting_value === 'true');
          }
        });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSamsara = async () => {
    setSaving(true);
    try {
      // Upsert API key
      const { error: apiKeyError } = await supabase
        .from('system_settings')
        .upsert({
          setting_key: 'samsara_api_key',
          setting_value: samsaraApiKey,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'setting_key'
        });

      if (apiKeyError) throw apiKeyError;

      // Upsert enabled status
      const { error: enabledError } = await supabase
        .from('system_settings')
        .upsert({
          setting_key: 'samsara_enabled',
          setting_value: samsaraEnabled.toString(),
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'setting_key'
        });

      if (enabledError) throw enabledError;

      setSamsaraConnected(true);
      toast.success('Samsara integration saved successfully!');
    } catch (error) {
      console.error('Error saving Samsara settings:', error);
      toast.error('Failed to save Samsara settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSamsara = async () => {
    setSaving(true);
    try {
      const result = await testSamsaraConnection();
      
      if (result.success) {
        setSamsaraConnected(true);
        toast.success('Samsara connection test successful!');
      } else {
        setSamsaraConnected(false);
        toast.error(`Samsara connection failed: ${result.error}`);
      }
    } catch (error) {
      console.error('Error testing Samsara:', error);
      toast.error('Failed to test Samsara connection');
    } finally {
      setSaving(false);
    }
  };


  const handleSaveELD = async () => {
    setSaving(true);
    try {
      // Upsert provider
      const { error: providerError } = await supabase
        .from('system_settings')
        .upsert({
          setting_key: 'eld_provider',
          setting_value: eldProvider,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'setting_key'
        });

      if (providerError) throw providerError;

      // Save EVA ELD specific fields
      if (eldProvider === 'EVA_ELD') {
        const { error: apiKeyError } = await supabase
          .from('system_settings')
          .upsert({
            setting_key: 'eld_api_key',
            setting_value: eldApiKey,
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'setting_key'
          });

        if (apiKeyError) throw apiKeyError;

        const { error: urlError } = await supabase
          .from('system_settings')
          .upsert({
            setting_key: 'eld_api_url',
            setting_value: eldApiUrl,
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'setting_key'
          });

        if (urlError) throw urlError;
      }

      // Save PRO LOGS specific fields
      if (eldProvider === 'PRO_LOGS') {
        const settings = [
          { setting_key: 'eld_prologs_name', setting_value: eldProLogsName },
          { setting_key: 'eld_prologs_id', setting_value: eldProLogsId },
          { setting_key: 'eld_prologs_secret', setting_value: eldProLogsSecret },
        ];

        for (const setting of settings) {
          const { error } = await supabase
            .from('system_settings')
            .upsert({
              ...setting,
              updated_at: new Date().toISOString()
            }, {
              onConflict: 'setting_key'
            });

          if (error) throw error;
        }
      }

      // Upsert enabled status
      const { error: enabledError } = await supabase
        .from('system_settings')
        .upsert({
          setting_key: 'eld_enabled',
          setting_value: eldEnabled.toString(),
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'setting_key'
        });

      if (enabledError) throw enabledError;

      // Set connected status based on provider
      if (eldProvider === 'EVA_ELD') {
        setEldConnected(!!eldApiKey && !!eldApiUrl);
      } else if (eldProvider === 'PRO_LOGS') {
        setEldConnected(!!eldProLogsName && !!eldProLogsId && !!eldProLogsSecret);
      }
      
      toast.success('ELD integration saved successfully!');
    } catch (error) {
      console.error('Error saving ELD settings:', error);
      toast.error('Failed to save ELD settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestELD = async () => {
    setSaving(true);
    try {
      if (eldProvider === 'EVA_ELD') {
        if (!eldApiKey || !eldApiUrl) {
          toast.error('Please enter API Key and API URL');
          return;
        }

        const { testELDConnection } = await import('../../services/eldService');
        const result = await testELDConnection(eldApiUrl, eldApiKey);
        
        if (result.success) {
          setEldConnected(true);
          toast.success('ELD connection test successful!');
        } else {
          setEldConnected(false);
          toast.error(`ELD connection failed: ${result.error}`);
        }
      } else if (eldProvider === 'PRO_LOGS') {
        if (!eldProLogsId || !eldProLogsSecret) {
          toast.error('Please enter ProLogs ID and Secret');
          return;
        }

        const { testProLogsConnection } = await import('../../services/eldService');
        const result = await testProLogsConnection();
        
        if (result.success) {
          setEldConnected(true);
          toast.success(`ProLogs connection successful! Found ${result.data?.length || 0} trucks.`);
        } else {
          setEldConnected(false);
          toast.error(`ProLogs connection failed: ${result.error}`);
        }
      } else {
        toast.error('Please select an ELD provider');
      }
    } catch (error: any) {
      console.error('Error testing ELD:', error);
      toast.error('Failed to test ELD connection');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveGHL = async () => {
    setSaving(true);
    try {
      // Upsert API key
      const { error: apiKeyError } = await supabase
        .from('system_settings')
        .upsert({
          setting_key: 'gohighlevel_api_key',
          setting_value: ghlApiKey,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'setting_key'
        });

      if (apiKeyError) throw apiKeyError;

      // Upsert Location ID
      const { error: locationError } = await supabase
        .from('system_settings')
        .upsert({
          setting_key: 'gohighlevel_location_id',
          setting_value: ghlLocationId,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'setting_key'
        });

      if (locationError) throw locationError;

      // Upsert enabled status
      const { error: enabledError } = await supabase
        .from('system_settings')
        .upsert({
          setting_key: 'gohighlevel_enabled',
          setting_value: ghlEnabled.toString(),
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'setting_key'
        });

      if (enabledError) throw enabledError;

      setGhlConnected(!!ghlApiKey && !!ghlLocationId);
      toast.success('GoHighLevel SMS integration saved successfully!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestSMS = async () => {
    if (!testPhone) {
      toast.error('Please enter a phone number to test');
      return;
    }

    if (!ghlApiKey || !ghlLocationId) {
      toast.error('Please save your GoHighLevel configuration first');
      return;
    }

    setIsTesting(true);
    setTestResult(null);

    try {
      const formattedPhone = formatPhoneNumber(testPhone);
      const testMessage = `🚛 TMS Test Message\n\nThis is a test SMS from your Transportation Management System.\n\nIf you received this message, your GoHighLevel SMS integration is working correctly!\n\nTimestamp: ${new Date().toLocaleString()}`;

      const result = await sendSMS({
        phone: formattedPhone,
        message: testMessage,
      });

      if (result.success) {
        setTestResult({
          success: true,
          message: 'Test SMS sent successfully! Check your phone.',
        });
        toast.success('Test SMS sent!');
      } else {
        setTestResult({
          success: false,
          message: result.error || 'Failed to send test SMS. Please check your configuration.',
        });
        toast.error('Failed to send test SMS');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setTestResult({
        success: false,
        message: `Error: ${errorMessage}`,
      });
      toast.error(errorMessage);
    } finally {
      setIsTesting(false);
    }
  };

  const handleSaveChatGPT = async () => {
    setSaving(true);
    try {
      // Upsert API key
      const { error: apiKeyError } = await supabase
        .from('system_settings')
        .upsert({
          setting_key: 'chatgpt_api_key',
          setting_value: chatgptApiKey,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'setting_key'
        });

      if (apiKeyError) throw apiKeyError;

      // Upsert model
      const { error: modelError } = await supabase
        .from('system_settings')
        .upsert({
          setting_key: 'chatgpt_model',
          setting_value: chatgptModel,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'setting_key'
        });

      if (modelError) throw modelError;

      // Upsert enabled status
      const { error: enabledError } = await supabase
        .from('system_settings')
        .upsert({
          setting_key: 'chatgpt_enabled',
          setting_value: chatgptEnabled.toString(),
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'setting_key'
        });

      if (enabledError) throw enabledError;

      setChatgptConnected(!!chatgptApiKey);
      toast.success('ChatGPT AI Assistant integration saved successfully!');
    } catch (error) {
      console.error('Error saving ChatGPT settings:', error);
      toast.error('Failed to save ChatGPT settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveGemini = async () => {
    setSaving(true);
    try {
      // Upsert API key
      const { error: apiKeyError } = await supabase
        .from('system_settings')
        .upsert({
          setting_key: 'gemini_api_key',
          setting_value: geminiApiKey,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'setting_key'
        });

      if (apiKeyError) throw apiKeyError;

      // Upsert model
      const { error: modelError } = await supabase
        .from('system_settings')
        .upsert({
          setting_key: 'gemini_model',
          setting_value: geminiModel,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'setting_key'
        });

      if (modelError) throw modelError;

      // Upsert enabled status
      const { error: enabledError } = await supabase
        .from('system_settings')
        .upsert({
          setting_key: 'gemini_enabled',
          setting_value: geminiEnabled.toString(),
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'setting_key'
        });

      if (enabledError) throw enabledError;

      setGeminiConnected(!!geminiApiKey);
      toast.success('Google Gemini integration saved successfully!');
    } catch (error) {
      console.error('Error saving Gemini settings:', error);
      toast.error('Failed to save Gemini settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestDocumentAI = async () => {
    setSaving(true);
    try {
      // Validate required fields
      if (!gcpProjectId || !gcpProcessorId || !gcpServiceAccountEmail || !gcpPrivateKey) {
        toast.error('Please fill in all required fields before testing');
        setSaving(false);
        return;
      }

      // Simple validation test - check if credentials are properly formatted
      try {
        // Trim whitespace
        const trimmedKey = gcpPrivateKey.trim();
        
        // Check if it starts with { (valid JSON)
        if (!trimmedKey.startsWith('{')) {
          const firstChar = trimmedKey.charAt(0);
          const firstChars = trimmedKey.substring(0, 20);
          throw new Error(`Private key must start with { but it starts with "${firstChar}". First 20 chars: "${firstChars}". Please paste the entire JSON key file contents (starting with { and ending with }).`);
        }

        // Validate private key format (should be valid JSON with private_key field)
        const keyData = JSON.parse(trimmedKey);
        
        if (!keyData.private_key || !keyData.client_email) {
          throw new Error('JSON key file must contain "private_key" and "client_email" fields. Make sure you pasted the complete service account JSON key.');
        }

        // Validate the private key itself
        if (!keyData.private_key.includes('BEGIN PRIVATE KEY')) {
          throw new Error('The private_key field must contain a valid PEM-formatted key (-----BEGIN PRIVATE KEY-----)');
        }

        // Validate email format
        const isValidServiceAccount = gcpServiceAccountEmail.includes('@') && 
          (gcpServiceAccountEmail.includes('.iam.gserviceaccount.com') || 
           gcpServiceAccountEmail.includes('compute@developer.gserviceaccount.com') ||
           gcpServiceAccountEmail.includes('.gserviceaccount.com'));
        
        if (!isValidServiceAccount) {
          throw new Error('Service account email must be a valid Google Cloud service account (ending with .gserviceaccount.com)');
        }

        // Validate processor ID format (should be alphanumeric)
        if (!/^[a-zA-Z0-9]+$/.test(gcpProcessorId)) {
          throw new Error('Processor ID should only contain letters and numbers');
        }

        setDocumentAIConnected(true);
        toast.success('Google Document AI credentials are valid! Connection test passed.');
      } catch (parseError: any) {
        setDocumentAIConnected(false);
        throw new Error(`Invalid credentials: ${parseError.message}`);
      }
    } catch (error: any) {
      console.error('Error testing Document AI:', error);
      setDocumentAIConnected(false);
      toast.error(error.message || 'Failed to test Document AI connection');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDocumentAI = async () => {
    setSaving(true);
    try {
      // Validate required fields
      if (documentAIEnabled && (!gcpProjectId || !gcpProcessorId || !gcpServiceAccountEmail || !gcpPrivateKey)) {
        toast.error('Please fill in all required fields for Google Document AI');
        setSaving(false);
        return;
      }

      // Upsert all GCP settings
      const settings = [
        { setting_key: 'gcp_project_id', setting_value: gcpProjectId },
        { setting_key: 'gcp_processor_id', setting_value: gcpProcessorId },
        { setting_key: 'gcp_service_account_email', setting_value: gcpServiceAccountEmail },
        { setting_key: 'gcp_private_key', setting_value: gcpPrivateKey },
        { setting_key: 'document_ai_enabled', setting_value: documentAIEnabled.toString() },
      ];

      for (const setting of settings) {
        const { error } = await supabase
          .from('system_settings')
          .upsert({
            ...setting,
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'setting_key'
          });

        if (error) throw error;
      }

      setDocumentAIConnected(!!gcpPrivateKey);
      toast.success('Google Document AI integration saved successfully!');
    } catch (error) {
      console.error('Error saving Document AI settings:', error);
      toast.error('Failed to save Document AI settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveOpenWeather = async () => {
    setSaving(true);
    try {
      // Upsert API key
      const { error: apiKeyError } = await supabase
        .from('system_settings')
        .upsert({
          setting_key: 'openweather_api_key',
          setting_value: openWeatherApiKey,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'setting_key'
        });

      if (apiKeyError) throw apiKeyError;

      // Upsert enabled status
      const { error: enabledError } = await supabase
        .from('system_settings')
        .upsert({
          setting_key: 'openweather_enabled',
          setting_value: openWeatherEnabled.toString(),
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'setting_key'
        });

      if (enabledError) throw enabledError;

      setOpenWeatherConnected(!!openWeatherApiKey);
      toast.success('OpenWeatherMap integration saved successfully!');
    } catch (error) {
      console.error('Error saving OpenWeatherMap settings:', error);
      toast.error('Failed to save OpenWeatherMap settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRingCentral = async () => {
    setSaving(true);
    try {
      // Upsert all RingCentral settings
      const settings = [
        { setting_key: 'ringcentral_client_id', setting_value: ringCentralClientId },
        { setting_key: 'ringcentral_client_secret', setting_value: ringCentralClientSecret },
        { setting_key: 'ringcentral_jwt', setting_value: ringCentralJwt },
        { setting_key: 'ringcentral_enabled', setting_value: ringCentralEnabled.toString() },
      ];

      for (const setting of settings) {
        const { error } = await supabase
          .from('system_settings')
          .upsert({
            ...setting,
            updated_at: new Date().toISOString()
          }, {
            onConflict: 'setting_key'
          });

        if (error) throw error;
      }

      setRingCentralConnected(!!ringCentralJwt);
      toast.success('RingCentral integration saved successfully!');
    } catch (error) {
      console.error('Error saving RingCentral settings:', error);
      toast.error('Failed to save RingCentral settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveGoogleMaps = async () => {
    setSaving(true);
    try {
      const { error: apiKeyError } = await supabase
        .from('system_settings')
        .upsert({
          setting_key: 'google_maps_api_key',
          setting_value: googleMapsApiKey,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'setting_key'
        });

      if (apiKeyError) throw apiKeyError;

      const { error: enabledError } = await supabase
        .from('system_settings')
        .upsert({
          setting_key: 'google_maps_enabled',
          setting_value: googleMapsEnabled.toString(),
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'setting_key'
        });

      if (enabledError) throw enabledError;

      setGoogleMapsConnected(!!googleMapsApiKey);
      toast.success('Google Maps API integration saved successfully!');
    } catch (error) {
      console.error('Error saving Google Maps settings:', error);
      toast.error('Failed to save Google Maps settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestGoogleMaps = async () => {
    setSaving(true);
    try {
      if (!googleMapsApiKey) {
        toast.error('Please enter a Google Maps API key');
        setSaving(false);
        return;
      }

      const testAddress = 'New York, NY';
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(testAddress)}&key=${googleMapsApiKey}`
      );

      const data = await response.json();

      if (data.status === 'OK') {
        setGoogleMapsConnected(true);
        toast.success('Google Maps API connection successful!');
      } else if (data.status === 'REQUEST_DENIED') {
        setGoogleMapsConnected(false);
        toast.error(`API key invalid: ${data.error_message || 'Request denied'}`);
      } else {
        setGoogleMapsConnected(false);
        toast.error(`API test failed: ${data.status}`);
      }
    } catch (error) {
      console.error('Error testing Google Maps API:', error);
      setGoogleMapsConnected(false);
      toast.error('Failed to test Google Maps API connection');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTruFund = async () => {
    setSaving(true);
    try {
      const settings = [
        { setting_key: 'trufund_username', setting_value: trufundUsername },
        { setting_key: 'trufund_token', setting_value: trufundToken },
        { setting_key: 'trufund_enabled', setting_value: trufundEnabled.toString() },
      ];

      for (const setting of settings) {
        const { error } = await supabase
          .from('system_settings')
          .upsert({
            ...setting,
            updated_at: new Date().toISOString(),
          }, { onConflict: 'setting_key' });
        if (error) throw error;
      }

      setTrufundConnected(!!trufundUsername && !!trufundToken);
      toast.success('TruFund integration saved successfully!');
    } catch (error) {
      console.error('Error saving TruFund settings:', error);
      toast.error('Failed to save TruFund settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestTruFund = async () => {
    if (!trufundUsername || !trufundToken) {
      toast.error('Please enter username and token');
      return;
    }
    setSaving(true);
    try {
      const { uploadToTruFund } = await import('../../services/trufundService');
      const probeBase64 = btoa('test');
      const result = await uploadToTruFund({
        shipmentNum: `TEST-${Date.now()}`,
        fileName: 'connectivity_probe.txt',
        base64String: probeBase64,
        sequenceNumber: 1,
        totalFiles: 1,
        extraInfo: 'connectivity probe (will not be processed)',
        overrideUser: trufundUsername,
        overrideToken: trufundToken,
      });

      if (result.success) {
        setTrufundConnected(true);
        toast.success(`TruFund reachable. Response: ${JSON.stringify(result)}`);
      } else {
        setTrufundConnected(false);
        toast.error(`TruFund test failed: ${result.error || result.extra_info || 'Unknown'}`);
      }
    } catch (error: any) {
      console.error('Error testing TruFund:', error);
      setTrufundConnected(false);
      toast.error(`Failed to test TruFund: ${error?.message || error}`);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveResend = async () => {
    if (resendApiKey && !resendApiKey.startsWith('re_')) {
      toast.error('Invalid Resend API key — it should start with "re_"');
      return;
    }
    setSaving(true);
    try {
      const settings = [
        { setting_key: 'resend_api_key', setting_value: resendApiKey },
        { setting_key: 'resend_from_email', setting_value: resendFromEmail },
        { setting_key: 'resend_enabled', setting_value: resendEnabled.toString() },
      ];

      for (const setting of settings) {
        const { error } = await supabase
          .from('system_settings')
          .upsert({
            ...setting,
            updated_at: new Date().toISOString(),
          }, { onConflict: 'setting_key' });
        if (error) throw error;
      }

      setResendConnected(!!resendApiKey);
      toast.success('Resend email integration saved successfully!');
    } catch (error) {
      console.error('Error saving Resend settings:', error);
      toast.error('Failed to save Resend settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address to test');
      return;
    }

    if (!resendApiKey || !resendFromEmail) {
      toast.error('Please save your Resend configuration first');
      return;
    }

    setIsTestingEmail(true);
    setEmailTestResult(null);

    try {
      const { sendEmail, buildBrandedEmail } = await import('../../services/emailService');
      const result = await sendEmail({
        to: testEmail,
        subject: '🚛 TMS Test Email',
        fromName: 'CS NATIONAL TMS',
        html: buildBrandedEmail({
          heading: 'Email Integration Test',
          bodyHtml: `
            <p style="margin: 0 0 16px;">This is a test email from your Transportation Management System.</p>
            <p style="margin: 0 0 16px;">If you received this message, your <strong>Resend</strong> email integration is working correctly and ready to send invoices, statements, and notifications.</p>`,
          footnote: `&#128231; Sent from Settings &rarr; Integrations on ${new Date().toLocaleString()}.`,
        }),
      });

      if (result.success) {
        setResendConnected(true);
        setEmailTestResult({
          success: true,
          message: 'Test email sent successfully! Check the inbox (and spam folder).',
        });
        toast.success('Test email sent!');
      } else {
        setEmailTestResult({
          success: false,
          message: result.error || 'Failed to send test email. Please check your configuration.',
        });
        toast.error('Failed to send test email');
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      setEmailTestResult({ success: false, message: `Error: ${errorMessage}` });
      toast.error(errorMessage);
    } finally {
      setIsTestingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl shadow-lg p-8 text-white">
          <h2 className="text-2xl font-bold mb-2">Integrations</h2>
          <p className="text-slate-300">
            Connect third-party services to enhance your TMS capabilities
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-slate-200 rounded w-1/4"></div>
            <div className="h-32 bg-slate-200 rounded"></div>
            <div className="h-32 bg-slate-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-xl shadow-lg p-8 text-white">
        <h2 className="text-2xl font-bold mb-2">Integrations</h2>
        <p className="text-slate-300">
          Connect third-party services to enhance your TMS capabilities
        </p>
      </div>

      {/* Integration Status Overview */}
      <IntegrationsOverview />

      {/* GoHighLevel SMS Integration */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-200 bg-slate-50">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="bg-green-100 p-3 rounded-lg">
                <MessageSquare className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-1">GoHighLevel SMS Notifications</h3>
                <p className="text-sm text-slate-600 mb-2">
                  Send SMS notifications to drivers for load assignments
                </p>
                <div className="flex items-center gap-2">
                  {ghlConnected ? (
                    <span className="flex items-center gap-1 text-sm text-green-600 font-medium">
                      <CheckCircle className="w-4 h-4" />
                      Connected
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-sm text-slate-500">
                      <XCircle className="w-4 h-4" />
                      Not Connected
                    </span>
                  )}
                </div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={ghlEnabled}
                onChange={(e) => setGhlEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
            </label>
          </div>
        </div>

        {ghlEnabled && (
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <Key className="w-4 h-4 inline mr-1" />
                GoHighLevel API Key
              </label>
              <div className="relative">
                <input
                  type={showGhlKey ? 'text' : 'password'}
                  value={ghlApiKey}
                  onChange={(e) => setGhlApiKey(e.target.value)}
                  placeholder="Enter your GoHighLevel API key"
                  className="w-full px-4 py-2.5 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <button
                  onClick={() => setShowGhlKey(!showGhlKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showGhlKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Get your API key from GoHighLevel Settings → API & Integrations
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Location ID
              </label>
              <input
                type="text"
                value={ghlLocationId}
                onChange={(e) => setGhlLocationId(e.target.value)}
                placeholder="Enter your GoHighLevel Location ID"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
              <p className="mt-1 text-xs text-slate-500">
                Find your Location ID in GoHighLevel Settings → Business Profile
              </p>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-green-900 mb-2">What you'll get:</h4>
              <ul className="text-sm text-green-800 space-y-1">
                <li>• Automatic SMS notifications on load assignment</li>
                <li>• Load update notifications to drivers</li>
                <li>• Cancellation alerts</li>
                <li>• Custom urgent dispatch messages</li>
              </ul>
            </div>

            <div className="border-t border-slate-200 pt-4">
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <Send className="w-4 h-4 inline mr-1" />
                Test SMS Configuration
              </label>
              <div className="flex gap-2">
                <input
                  type="tel"
                  value={testPhone}
                  onChange={(e) => setTestPhone(e.target.value)}
                  placeholder="Enter phone number (e.g., +1234567890)"
                  className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                />
                <button
                  onClick={handleTestSMS}
                  disabled={isTesting}
                  className="px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isTesting ? 'Sending...' : 'Test'}
                </button>
              </div>
              
              {testResult && (
                <div className={`mt-2 p-2 rounded flex items-start gap-2 text-sm ${
                  testResult.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {testResult.success ? (
                    <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  )}
                  <p>{testResult.message}</p>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={handleSaveGHL}
                disabled={!ghlApiKey || !ghlLocationId || saving}
                className="flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Samsara GPS Integration */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-200 bg-slate-50">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="bg-blue-100 p-3 rounded-lg">
                <MapPin className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-1">Samsara GPS Tracking</h3>
                <p className="text-sm text-slate-600 mb-2">
                  Real-time GPS tracking and fleet management integration
                </p>
                <div className="flex items-center gap-2">
                  {samsaraConnected ? (
                    <span className="flex items-center gap-1 text-sm text-green-600 font-medium">
                      <CheckCircle className="w-4 h-4" />
                      Connected
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-sm text-slate-500">
                      <XCircle className="w-4 h-4" />
                      Not Connected
                    </span>
                  )}
                </div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={samsaraEnabled}
                onChange={(e) => setSamsaraEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>

        {samsaraEnabled && (
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Samsara API Key
              </label>
              <div className="relative">
                <input
                  type={showSamsaraKey ? 'text' : 'password'}
                  value={samsaraApiKey}
                  onChange={(e) => setSamsaraApiKey(e.target.value)}
                  placeholder="Enter your Samsara API key"
                  className="w-full px-4 py-2.5 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  onClick={() => setShowSamsaraKey(!showSamsaraKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showSamsaraKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Get your API key from{' '}
                <a
                  href="https://cloud.samsara.com/settings/api-tokens"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:underline"
                >
                  Samsara Dashboard → Settings → API Tokens
                </a>
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-900 mb-2">What you'll get:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• Real-time GPS location tracking for all trucks</li>
                <li>• Driver behavior and safety scores</li>
                <li>• Fuel consumption and efficiency data</li>
                <li>• Maintenance alerts and diagnostics</li>
                <li>• Route history and geofencing</li>
              </ul>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={handleSaveSamsara}
                disabled={!samsaraApiKey || saving}
                className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
              <button
                onClick={handleTestSamsara}
                disabled={!samsaraApiKey || saving}
                className="flex items-center gap-2 px-6 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Test Connection
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ELD Integration */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-200 bg-slate-50">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="bg-green-100 p-3 rounded-lg">
                <Activity className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-1">ELD (Electronic Logging Device)</h3>
                <p className="text-sm text-slate-600 mb-2">
                  Hours of Service (HOS) compliance and driver logs integration
                </p>
                <div className="flex items-center gap-2">
                  {eldConnected ? (
                    <span className="flex items-center gap-1 text-sm text-green-600 font-medium">
                      <CheckCircle className="w-4 h-4" />
                      Connected
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-sm text-slate-500">
                      <XCircle className="w-4 h-4" />
                      Not Connected
                    </span>
                  )}
                </div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={eldEnabled}
                onChange={(e) => setEldEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
            </label>
          </div>
        </div>

        {eldEnabled && (
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                ELD Provider
              </label>
              <select
                value={eldProvider}
                onChange={(e) => setEldProvider(e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="">Select ELD Provider</option>
                <option value="EVA_ELD">EVA ELD (AAA ELD)</option>
                <option value="PRO_LOGS">PRO LOGS</option>
              </select>
              <p className="mt-1 text-xs text-slate-500">
                Select your ELD provider brand
              </p>
            </div>

            {/* EVA ELD Fields */}
            {eldProvider === 'EVA_ELD' && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    API Base URL
                  </label>
                  <input
                    type="text"
                    value={eldApiUrl}
                    onChange={(e) => setEldApiUrl(e.target.value)}
                    placeholder="https://cloud.evaeld.com"
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    The base URL for EVA ELD API
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    API Key / Access Token
                  </label>
                  <div className="relative">
                    <input
                      type={showEldKey ? 'text' : 'password'}
                      value={eldApiKey}
                      onChange={(e) => setEldApiKey(e.target.value)}
                      placeholder="Enter your EVA ELD API key"
                      className="w-full px-4 py-2.5 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                    <button
                      onClick={() => setShowEldKey(!showEldKey)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showEldKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    Get your API key from EVA ELD dashboard
                  </p>
                </div>
              </>
            )}

            {/* PRO LOGS Fields */}
            {eldProvider === 'PRO_LOGS' && (
              <>
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Name
                  </label>
                  <input
                    type="text"
                    value={eldProLogsName}
                    onChange={(e) => setEldProLogsName(e.target.value)}
                    placeholder="Enter PRO LOGS Name"
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    Your company name (optional, for reference only)
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Client ID
                  </label>
                  <input
                    type="text"
                    value={eldProLogsId}
                    onChange={(e) => setEldProLogsId(e.target.value)}
                    placeholder="e22d625e-b4c1-4522-acc0-21b4afe1469d"
                    className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    OAuth 2.0 Client ID from ProLogs
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Client Secret
                  </label>
                  <div className="relative">
                    <input
                      type={showProLogsSecret ? 'text' : 'password'}
                      value={eldProLogsSecret}
                      onChange={(e) => setEldProLogsSecret(e.target.value)}
                      placeholder="Enter PRO LOGS Client Secret"
                      className="w-full px-4 py-2.5 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    />
                    <button
                      onClick={() => setShowProLogsSecret(!showProLogsSecret)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showProLogsSecret ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  <p className="mt-1 text-xs text-slate-500">
                    OAuth 2.0 Client Secret from ProLogs dashboard
                  </p>
                </div>
              </>
            )}

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-green-900 mb-2">What you'll get:</h4>
              <ul className="text-sm text-green-800 space-y-1">
                <li>• Real-time GPS location tracking for all trucks</li>
                <li>• Hours of Service (HOS) tracking</li>
                <li>• Driver availability and duty status</li>
                <li>• Violation alerts and warnings</li>
                <li>• Electronic logs for audits</li>
              </ul>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={handleSaveELD}
                disabled={saving || !eldApiKey || !eldApiUrl}
                className="flex items-center gap-2 px-6 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
              <button
                onClick={handleTestELD}
                disabled={saving || !eldApiKey || !eldApiUrl}
                className="flex items-center gap-2 px-6 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Test Connection
              </button>
            </div>

            {/* Multiple ProLogs Connections Manager */}
            {eldProvider === 'PRO_LOGS' && (
              <div className="mt-6 pt-6 border-t border-slate-200">
                <ELDConnectionsManager />
              </div>
            )}
          </div>
        )}
      </div>

      {/* ChatGPT AI Assistant Integration */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-200 bg-slate-50">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="bg-purple-100 p-3 rounded-lg">
                <Bot className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-1">ChatGPT AI Assistant</h3>
                <p className="text-sm text-slate-600 mb-2">
                  Intelligent AI assistant to help answer questions about your fleet data
                </p>
                <div className="flex items-center gap-2">
                  {chatgptConnected ? (
                    <span className="flex items-center gap-1 text-sm text-green-600 font-medium">
                      <CheckCircle className="w-4 h-4" />
                      Connected
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-sm text-slate-500">
                      <XCircle className="w-4 h-4" />
                      Not Connected
                    </span>
                  )}
                </div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={chatgptEnabled}
                onChange={(e) => setChatgptEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-purple-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
            </label>
          </div>
        </div>

        {chatgptEnabled && (
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <Key className="w-4 h-4 inline mr-1" />
                OpenAI API Key
              </label>
              <div className="relative">
                <input
                  type={showChatgptKey ? 'text' : 'password'}
                  value={chatgptApiKey}
                  onChange={(e) => setChatgptApiKey(e.target.value)}
                  placeholder="sk-..."
                  className="w-full px-4 py-2.5 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                />
                <button
                  onClick={() => setShowChatgptKey(!showChatgptKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showChatgptKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Get your API key from{' '}
                <a
                  href="https://platform.openai.com/api-keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-purple-600 hover:underline"
                >
                  OpenAI Platform → API Keys
                </a>
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                AI Model
              </label>
              <select
                value={chatgptModel}
                onChange={(e) => setChatgptModel(e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
              >
                <option value="gpt-4o-mini">GPT-4o Mini (Recommended - Fast & Cost-effective)</option>
                <option value="gpt-4o">GPT-4o (Most Capable)</option>
                <option value="gpt-3.5-turbo">GPT-3.5 Turbo (Legacy)</option>
              </select>
              <p className="mt-1 text-xs text-slate-500">
                Choose the AI model based on your needs and budget
              </p>
            </div>

            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-purple-900 mb-2">What you can ask:</h4>
              <ul className="text-sm text-purple-800 space-y-1">
                <li>• "How many maintenance records does truck #123 have?"</li>
                <li>• "What is the current location of truck ABC-1234?" (using Samsara)</li>
                <li>• "What maintenance is due for my fleet this week?"</li>
                <li>• "Show me trucks with the most issues"</li>
                <li>• "Which drivers have the most loads this month?"</li>
                <li>• "What's wrong with truck #456?"</li>
              </ul>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={handleSaveChatGPT}
                disabled={!chatgptApiKey || saving}
                className="flex items-center gap-2 px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Google Gemini AI Integration */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-200 bg-slate-50">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="bg-indigo-100 p-3 rounded-lg">
                <Sparkles className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-1">Google Gemini AI</h3>
                <p className="text-sm text-slate-600 mb-2">
                  Parse maintenance invoices and documents directly from PDF using Gemini's vision model
                </p>
                <div className="flex items-center gap-2">
                  {geminiConnected ? (
                    <span className="flex items-center gap-1 text-sm text-green-600 font-medium">
                      <CheckCircle className="w-4 h-4" />
                      Connected
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-sm text-slate-500">
                      <XCircle className="w-4 h-4" />
                      Not Connected
                    </span>
                  )}
                </div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={geminiEnabled}
                onChange={(e) => setGeminiEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
            </label>
          </div>
        </div>

        {geminiEnabled && (
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <Key className="w-4 h-4 inline mr-1" />
                Gemini API Key
              </label>
              <div className="relative">
                <input
                  type={showGeminiKey ? 'text' : 'password'}
                  value={geminiApiKey}
                  onChange={(e) => setGeminiApiKey(e.target.value)}
                  placeholder="AIza..."
                  className="w-full px-4 py-2.5 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <button
                  onClick={() => setShowGeminiKey(!showGeminiKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showGeminiKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Get your API key from{' '}
                <a
                  href="https://aistudio.google.com/app/apikey"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:underline"
                >
                  Google AI Studio → API Keys
                </a>
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                AI Model
              </label>
              <select
                value={geminiModel}
                onChange={(e) => setGeminiModel(e.target.value)}
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option value="gemini-2.5-flash">Gemini 2.5 Flash (Recommended - Fast & cost-effective)</option>
                <option value="gemini-2.5-pro">Gemini 2.5 Pro (Most capable)</option>
                <option value="gemini-2.5-flash-lite">Gemini 2.5 Flash-Lite (Cheapest)</option>
              </select>
              <p className="mt-1 text-xs text-slate-500">
                Gemini reads the PDF directly (no separate OCR step needed)
              </p>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={handleSaveGemini}
                disabled={!geminiApiKey || saving}
                className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Google Document AI Integration */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-200 bg-slate-50">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Bot className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-1">Google Document AI</h3>
                <p className="text-sm text-slate-600 mb-2">
                  Advanced OCR and document parsing for rate confirmations (Recommended)
                </p>
                <div className="flex items-center gap-2">
                  {documentAIConnected ? (
                    <span className="flex items-center gap-1 text-sm text-green-600 font-medium">
                      <CheckCircle className="w-4 h-4" />
                      Connected
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-sm text-slate-500">
                      <XCircle className="w-4 h-4" />
                      Not Connected
                    </span>
                  )}
                </div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={documentAIEnabled}
                onChange={(e) => setDocumentAIEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>
        </div>

        {documentAIEnabled && (
          <div className="p-6 space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <h4 className="text-sm font-semibold text-yellow-900 mb-2">📋 Setup Required</h4>
              <p className="text-sm text-yellow-800 mb-2">
                Follow the setup guide to configure Google Document AI:
              </p>
              <a
                href="/GOOGLE_DOCUMENT_AI_SETUP.md"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm text-blue-600 hover:underline font-medium"
              >
                View Setup Guide →
              </a>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                GCP Project ID *
              </label>
              <input
                type="text"
                value={gcpProjectId}
                onChange={(e) => setGcpProjectId(e.target.value)}
                placeholder="e.g., tms-document-ai"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-xs text-slate-500">
                Your Google Cloud Platform project ID
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Processor ID *
              </label>
              <input
                type="text"
                value={gcpProcessorId}
                onChange={(e) => setGcpProcessorId(e.target.value)}
                placeholder="e.g., 1234567890abcdef"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-xs text-slate-500">
                Document AI processor ID (from GCP Console)
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Service Account Email *
              </label>
              <input
                type="email"
                value={gcpServiceAccountEmail}
                onChange={(e) => setGcpServiceAccountEmail(e.target.value)}
                placeholder="e.g., tms-service@project.iam.gserviceaccount.com"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-xs text-slate-500">
                Service account email from your JSON key file
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <Key className="w-4 h-4 inline mr-1" />
                Private Key *
              </label>
              <div className="relative">
                <textarea
                  value={gcpPrivateKey}
                  onChange={(e) => setGcpPrivateKey(e.target.value)}
                  placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
                  rows={4}
                  className="w-full px-4 py-2.5 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-xs"
                  style={{ display: showGcpKey ? 'block' : 'none' }}
                />
                {!showGcpKey && (
                  <div className="w-full px-4 py-2.5 border border-slate-300 rounded-lg bg-slate-50 text-slate-400 text-sm">
                    ••••••••••••••••••••••••••••
                  </div>
                )}
                <button
                  onClick={() => setShowGcpKey(!showGcpKey)}
                  className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
                >
                  {showGcpKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Copy the entire private_key field from your JSON key file (including BEGIN/END lines)
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-blue-900 mb-2">✨ Benefits over PDF.co:</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• <strong>Better Accuracy:</strong> Superior OCR for scanned/image PDFs</li>
                <li>• <strong>Cost Savings:</strong> ~40-60% cheaper at scale (120 PDFs/day = $41/mo vs $72-108/mo)</li>
                <li>• <strong>Structured Data:</strong> Understands tables, forms, and document layout</li>
                <li>• <strong>Free Tier:</strong> 1,000 pages/month free</li>
                <li>• <strong>Enterprise Grade:</strong> Google Cloud reliability and security</li>
              </ul>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={handleTestDocumentAI}
                disabled={saving || !gcpProjectId || !gcpProcessorId || !gcpServiceAccountEmail || !gcpPrivateKey}
                className="flex items-center gap-2 px-6 py-2.5 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Test Connection
              </button>
              <button
                onClick={handleSaveDocumentAI}
                disabled={saving || !gcpProjectId || !gcpProcessorId || !gcpServiceAccountEmail || !gcpPrivateKey}
                className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* OpenWeatherMap Integration */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-200 bg-slate-50">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="bg-sky-100 p-3 rounded-lg">
                <Cloud className="w-6 h-6 text-sky-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-1">OpenWeatherMap Weather Radar</h3>
                <p className="text-sm text-slate-600 mb-2">
                  Live weather radar overlay on dashboard map showing precipitation, clouds, and temperature
                </p>
                <div className="flex items-center gap-2">
                  {openWeatherConnected ? (
                    <span className="flex items-center gap-1 text-sm text-green-600 font-medium">
                      <CheckCircle className="w-4 h-4" />
                      Connected
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-sm text-slate-500">
                      <XCircle className="w-4 h-4" />
                      Not Connected
                    </span>
                  )}
                </div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={openWeatherEnabled}
                onChange={(e) => setOpenWeatherEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-sky-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-sky-600"></div>
            </label>
          </div>
        </div>

        {openWeatherEnabled && (
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <Key className="w-4 h-4 inline mr-1" />
                OpenWeatherMap API Key
              </label>
              <div className="relative">
                <input
                  type={showOpenWeatherKey ? 'text' : 'password'}
                  value={openWeatherApiKey}
                  onChange={(e) => setOpenWeatherApiKey(e.target.value)}
                  placeholder="Enter your OpenWeatherMap API key"
                  className="w-full px-4 py-2.5 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                />
                <button
                  onClick={() => setShowOpenWeatherKey(!showOpenWeatherKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showOpenWeatherKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Get your free API key from{' '}
                <a
                  href="https://openweathermap.org/api"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sky-600 hover:underline"
                >
                  OpenWeatherMap → API Keys
                </a>
                {' '}(Free tier: 1,000 calls/day)
              </p>
            </div>

            <div className="bg-sky-50 border border-sky-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-sky-900 mb-2">What you'll get:</h4>
              <ul className="text-sm text-sky-800 space-y-1">
                <li>• <strong>Live Weather Radar:</strong> Real-time precipitation overlay on map</li>
                <li>• <strong>Cloud Coverage:</strong> See cloud formations and coverage</li>
                <li>• <strong>Temperature Zones:</strong> Color-coded temperature visualization</li>
                <li>• <strong>Snow & Rain Detection:</strong> Track weather conditions affecting routes</li>
                <li>• <strong>Storm Tracking:</strong> Monitor severe weather in real-time</li>
              </ul>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={handleSaveOpenWeather}
                disabled={!openWeatherApiKey || saving}
                className="flex items-center gap-2 px-6 py-2.5 bg-sky-600 text-white rounded-lg hover:bg-sky-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* RingCentral Integration */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-200 bg-slate-50">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="bg-orange-100 p-3 rounded-lg">
                <Phone className="w-6 h-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-1">RingCentral Communications</h3>
                <p className="text-sm text-slate-600 mb-2">
                  Cloud-based phone system for voice calls, SMS, and team messaging
                </p>
                <div className="flex items-center gap-2">
                  {ringCentralConnected ? (
                    <span className="flex items-center gap-1 text-sm text-green-600 font-medium">
                      <CheckCircle className="w-4 h-4" />
                      Connected
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-sm text-slate-500">
                      <XCircle className="w-4 h-4" />
                      Not Connected
                    </span>
                  )}
                </div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={ringCentralEnabled}
                onChange={(e) => setRingCentralEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-orange-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
            </label>
          </div>
        </div>

        {ringCentralEnabled && (
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <Key className="w-4 h-4 inline mr-1" />
                Client ID
              </label>
              <input
                type="text"
                value={ringCentralClientId}
                onChange={(e) => setRingCentralClientId(e.target.value)}
                placeholder="Enter your RingCentral Client ID"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              />
              <p className="mt-1 text-xs text-slate-500">
                Your RingCentral app Client ID
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <Key className="w-4 h-4 inline mr-1" />
                Client Secret
              </label>
              <div className="relative">
                <input
                  type={showRingCentralSecret ? 'text' : 'password'}
                  value={ringCentralClientSecret}
                  onChange={(e) => setRingCentralClientSecret(e.target.value)}
                  placeholder="Enter your RingCentral Client Secret"
                  className="w-full px-4 py-2.5 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                />
                <button
                  onClick={() => setShowRingCentralSecret(!showRingCentralSecret)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showRingCentralSecret ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Your RingCentral app Client Secret
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <Key className="w-4 h-4 inline mr-1" />
                JWT Token
              </label>
              <div className="relative">
                <textarea
                  value={ringCentralJwt}
                  onChange={(e) => setRingCentralJwt(e.target.value)}
                  placeholder="Enter your RingCentral JWT token"
                  rows={4}
                  className={`w-full px-4 py-2.5 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500 font-mono text-xs ${!showRingCentralJwt ? 'text-security-disc' : ''}`}
                  style={{ 
                    WebkitTextSecurity: showRingCentralJwt ? 'none' : 'disc',
                    fontFamily: showRingCentralJwt ? 'monospace' : 'inherit'
                  } as React.CSSProperties}
                />
                <button
                  type="button"
                  onClick={() => setShowRingCentralJwt(!showRingCentralJwt)}
                  className="absolute right-3 top-3 text-slate-400 hover:text-slate-600"
                >
                  {showRingCentralJwt ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Your RingCentral JWT authentication token
              </p>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-orange-900 mb-2">What you'll get:</h4>
              <ul className="text-sm text-orange-800 space-y-1">
                <li>• <strong>Cloud Phone System:</strong> Make and receive calls from anywhere</li>
                <li>• <strong>SMS Messaging:</strong> Send and receive text messages</li>
                <li>• <strong>Team Messaging:</strong> Internal team communication</li>
                <li>• <strong>Call Recording:</strong> Record and store important calls</li>
                <li>• <strong>Analytics:</strong> Track call metrics and performance</li>
                <li>• <strong>Mobile & Desktop Apps:</strong> Stay connected on any device</li>
              </ul>
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={handleSaveRingCentral}
                disabled={!ringCentralClientId || !ringCentralClientSecret || !ringCentralJwt || saving}
                className="flex items-center gap-2 px-6 py-2.5 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Google Maps API Integration */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-200 bg-slate-50">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="bg-red-100 p-3 rounded-lg">
                <MapPin className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-1">Google Maps API</h3>
                <p className="text-sm text-slate-600 mb-2">
                  Calculate accurate distances and empty miles using Google Maps routing
                </p>
                <div className="flex items-center gap-2">
                  {googleMapsConnected ? (
                    <span className="flex items-center gap-1 text-sm text-green-600 font-medium">
                      <CheckCircle className="w-4 h-4" />
                      Connected
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-sm text-slate-500">
                      <XCircle className="w-4 h-4" />
                      Not Connected
                    </span>
                  )}
                </div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={googleMapsEnabled}
                onChange={(e) => setGoogleMapsEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600"></div>
            </label>
          </div>
        </div>

        {googleMapsEnabled && (
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <Key className="w-4 h-4 inline mr-2" />
                Google Maps API Key
              </label>
              <div className="relative">
                <input
                  type={showGoogleMapsKey ? 'text' : 'password'}
                  value={googleMapsApiKey}
                  onChange={(e) => setGoogleMapsApiKey(e.target.value)}
                  placeholder="Enter your Google Maps API key"
                  className="w-full px-4 py-2.5 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                />
                <button
                  onClick={() => setShowGoogleMapsKey(!showGoogleMapsKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showGoogleMapsKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Get your API key from{' '}
                <a
                  href="https://console.cloud.google.com/apis/credentials"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-red-600 hover:underline"
                >
                  Google Cloud Console → Credentials
                </a>
              </p>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-red-900 mb-2">Required APIs:</h4>
              <ul className="text-sm text-red-800 space-y-1">
                <li>• <strong>Distance Matrix API</strong> - Calculate distances between locations</li>
                <li>• <strong>Geocoding API</strong> - Convert addresses to coordinates</li>
                <li>• <strong>Directions API</strong> - Get routing information</li>
              </ul>
              <p className="text-xs text-red-700 mt-2">
                💰 Google provides $200 free credit per month
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-amber-900 mb-2">⚙️ Setup Instructions:</h4>
              <ol className="text-sm text-amber-800 space-y-1 list-decimal list-inside">
                <li>Go to <strong>Google Cloud Console</strong></li>
                <li>Enable <strong>Distance Matrix API</strong>, <strong>Geocoding API</strong>, and <strong>Directions API</strong></li>
                <li>Create an <strong>API Key</strong> in Credentials</li>
                <li>Restrict the key to your domain for security</li>
                <li>Enable billing (required, but includes $200 free credit/month)</li>
              </ol>
            </div>

            <div className="flex gap-3 pt-4 border-t border-slate-200">
              <button
                onClick={handleTestGoogleMaps}
                disabled={saving || !googleMapsApiKey}
                className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors font-semibold"
              >
                <Activity className="w-4 h-4" />
                {saving ? 'Testing...' : 'Test Connection'}
              </button>
              <button
                onClick={handleSaveGoogleMaps}
                disabled={saving}
                className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors font-semibold"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* TruFund Factoring Integration */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-200 bg-slate-50">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="bg-emerald-100 p-3 rounded-lg">
                <Banknote className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-1">TruFund Factoring Upload</h3>
                <p className="text-sm text-slate-600 mb-2">
                  Submit invoice batches and supporting documents to TruFund's FactorSoft webservice
                </p>
                <div className="flex items-center gap-2">
                  {trufundConnected ? (
                    <span className="flex items-center gap-1 text-sm text-green-600 font-medium">
                      <CheckCircle className="w-4 h-4" />
                      Connected
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-sm text-slate-500">
                      <XCircle className="w-4 h-4" />
                      Not Connected
                    </span>
                  )}
                </div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={trufundEnabled}
                onChange={(e) => setTrufundEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-emerald-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
            </label>
          </div>
        </div>

        {trufundEnabled && (
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                Username
              </label>
              <input
                type="text"
                value={trufundUsername}
                onChange={(e) => setTrufundUsername(e.target.value)}
                placeholder="Same as your TruFund FOB web client login (use TEST for sandbox)"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              />
              <p className="mt-1 text-xs text-slate-500">
                Provided by TruFund — same as the FOB web client login.
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <Key className="w-4 h-4 inline mr-1" />
                API Token
              </label>
              <div className="relative">
                <input
                  type={showTrufundToken ? 'text' : 'password'}
                  value={trufundToken}
                  onChange={(e) => setTrufundToken(e.target.value)}
                  placeholder="Per-client token from TruFund admin email"
                  className="w-full px-4 py-2.5 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
                <button
                  onClick={() => setShowTrufundToken(!showTrufundToken)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showTrufundToken ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Each client has their own token; sent separately by TruFund admin.
              </p>
            </div>

            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-emerald-900 mb-2">How it works</h4>
              <ul className="text-sm text-emerald-800 space-y-1">
                <li>• Endpoint: <code className="bg-white px-1 rounded">clients.trufunding.net:85/WebService/FactorSoftWebService.asmx</code></li>
                <li>• Each shipment must include exactly one Excel summary as file 1, then PDFs</li>
                <li>• Files are sent sequentially (sequence_number = 1..total_files)</li>
                <li>• Server processing triggers when sequence_number == total_files</li>
                <li>• Max 15MB per file, no async uploads</li>
              </ul>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                onClick={handleTestTruFund}
                disabled={saving || !trufundUsername || !trufundToken}
                className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors font-semibold"
              >
                <Activity className="w-4 h-4" />
                {saving ? 'Testing...' : 'Test Connection'}
              </button>
              <button
                onClick={handleSaveTruFund}
                disabled={saving}
                className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-slate-900 text-white rounded-lg hover:bg-slate-800 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors font-semibold"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Resend Email Integration */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-6 border-b border-slate-200 bg-slate-50">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-4">
              <div className="bg-indigo-100 p-3 rounded-lg">
                <Mail className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900 mb-1">Resend Email</h3>
                <p className="text-sm text-slate-600 mb-2">
                  Send transactional emails (invoices, statements, notifications) via Resend
                </p>
                <div className="flex items-center gap-2">
                  {resendConnected ? (
                    <span className="flex items-center gap-1 text-sm text-green-600 font-medium">
                      <CheckCircle className="w-4 h-4" />
                      Connected
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-sm text-slate-500">
                      <XCircle className="w-4 h-4" />
                      Not Connected
                    </span>
                  )}
                </div>
              </div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={resendEnabled}
                onChange={(e) => setResendEnabled(e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-slate-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-slate-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
            </label>
          </div>
        </div>

        {resendEnabled && (
          <div className="p-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <Key className="w-4 h-4 inline mr-1" />
                Resend API Key
              </label>
              <div className="relative">
                <input
                  type={showResendKey ? 'text' : 'password'}
                  value={resendApiKey}
                  onChange={(e) => setResendApiKey(e.target.value)}
                  placeholder="re_..."
                  className="w-full px-4 py-2.5 pr-10 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <button
                  onClick={() => setShowResendKey(!showResendKey)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showResendKey ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Get your API key from{' '}
                <a
                  href="https://resend.com/api-keys"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:underline"
                >
                  Resend Dashboard → API Keys
                </a>
                {' '}(starts with re_)
              </p>
            </div>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <Mail className="w-4 h-4 inline mr-1" />
                From Email Address
              </label>
              <input
                type="email"
                value={resendFromEmail}
                onChange={(e) => setResendFromEmail(e.target.value)}
                placeholder="reminder@tmstaraexpress.com"
                className="w-full px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              />
              <p className="mt-1 text-xs text-slate-500">
                Must be on a domain verified in{' '}
                <a
                  href="https://resend.com/domains"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-indigo-600 hover:underline"
                >
                  Resend → Domains
                </a>
              </p>
            </div>

            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <h4 className="text-sm font-semibold text-indigo-900 mb-2">What you'll get:</h4>
              <ul className="text-sm text-indigo-800 space-y-1">
                <li>• Email invoices and statements to customers</li>
                <li>• Driver and dispatch email notifications</li>
                <li>• Document delivery (rate confirmations, BOLs)</li>
                <li>• Reliable delivery with open/bounce tracking in Resend dashboard</li>
              </ul>
            </div>

            <div className="border-t border-slate-200 pt-4">
              <label className="block text-sm font-semibold text-slate-700 mb-2">
                <Send className="w-4 h-4 inline mr-1" />
                Send Test Email
              </label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                  placeholder="Enter email address (e.g., you@example.com)"
                  className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
                <button
                  onClick={handleTestEmail}
                  disabled={isTestingEmail}
                  className="px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isTestingEmail ? 'Sending...' : 'Test'}
                </button>
              </div>
              <p className="mt-1 text-xs text-slate-500">
                Save your configuration first — the test sends using the saved settings.
              </p>

              {emailTestResult && (
                <div className={`mt-2 p-2 rounded flex items-start gap-2 text-sm ${
                  emailTestResult.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {emailTestResult.success ? (
                    <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  )}
                  <p>{emailTestResult.message}</p>
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={handleSaveResend}
                disabled={!resendApiKey || saving}
                className="flex items-center gap-2 px-6 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Save className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">💡 Integration Tips</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• API keys are stored securely and encrypted</li>
          <li>• Data syncs automatically every 5 minutes</li>
          <li>• You can view GPS locations on the Dispatch Board</li>
          <li>• ELD data appears in Driver and Truck details</li>
          <li>• Test connections before saving to ensure proper setup</li>
          <li>• <strong>Document AI is recommended</strong> for better accuracy and lower costs</li>
          <li>• <strong>Weather radar</strong> appears on the Dashboard map when enabled</li>
          <li>• <strong>Google Maps</strong> provides more accurate distance calculations than OSRM</li>
        </ul>
      </div>
    </div>
  );
}
